/* This context is used for storing state that may need to be accessed from anywhere in the app,
or that we need to keep track on for the entirety of a user session. */
import { createContext, ReactNode, useContext, useState } from 'react';

interface ISessionState {
  watersSignupWallDisplayed: boolean;
  numberOfWatersViewed: number;
  initialClientRenderComplete: boolean;
  incrementNumberOfWatersViewed: () => void;
  setWatersSignupWallDisplayed: (isDisplayed: boolean) => void;
  setInitialClientRenderComplete: (isComplete: boolean) => void;
}

const SessionContext = createContext<ISessionState>({
  watersSignupWallDisplayed: false,
  numberOfWatersViewed: 0,
  initialClientRenderComplete: false,
  incrementNumberOfWatersViewed: () => undefined,
  setWatersSignupWallDisplayed: (_: boolean) => undefined,
  setInitialClientRenderComplete: (_: boolean) => undefined,
});

export interface ISessionProviderProps {
  children: ReactNode;
  // Can be used for unit tests. If provided, all useEffects with mutations or data fetches will be
  // skipped at the values added to testState will override the context response.
  testState?: Partial<ISessionState>;
}

function SessionProvider({ testState = {}, ...props }: ISessionProviderProps) {
  const [numberOfWatersViewed, setNumberOfWatersViewed] = useState(0);
  const [initialClientRenderComplete, setInitialClientRenderComplete] = useState(false);
  const [watersSignupWallDisplayed, setWatersSignupWallDisplayed] = useState(false);

  return (
    <SessionContext.Provider
      {...props}
      value={{
        numberOfWatersViewed,
        initialClientRenderComplete,
        watersSignupWallDisplayed,
        incrementNumberOfWatersViewed: () => {
          setNumberOfWatersViewed(numberOfWatersViewed + 1);
        },
        setWatersSignupWallDisplayed,
        setInitialClientRenderComplete,
        ...testState,
      }}
    />
  );
}

const useSession = (): ISessionState => useContext(SessionContext);

export { SessionProvider, useSession };
